import React, { useState, useCallback, useEffect } from 'react';
import debounce from 'lodash.debounce';
import axios from '../../utils/axiosConfig';
import { setAuthToken } from '../../utils/auth';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { InfoTooltip } from '../Common/InfoTooltip';
import '../../styles/BookingManagement.css';

function BookingManagement() {
  const [notificationEmail, setNotificationEmail] = useState('');
  const [ccEmails, setCcEmails] = useState([]);
  const [ccEmailInput, setCcEmailInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const queryClient = useQueryClient();
  const ITEMS_PER_PAGE = 10;
  const [approvingBookings, setApprovingBookings] = useState({});
  const [toast, setToast] = useState(null);
  const [viewMode, setViewMode] = useState('upcoming');
  const [emailSettingsExpanded, setEmailSettingsExpanded] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
    }
  }, []);

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => {
        setToast(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);


  const { data: emailSettings, isLoading: isLoadingEmailSettings } = useQuery(
    'emailSettings',
    () => axios.get('/api/bookings/vendor/email-settings').then(res => res.data),
    {
      onSuccess: (data) => {
        setNotificationEmail(data.notification_email || '');
        // Split the CC emails by commas and trim whitespace
        if (data.cc_emails) {
          const emailsArray = data.cc_emails
            .split(',')
            .map(email => email.trim())
            .filter(email => email.length > 0);
          setCcEmails(emailsArray);
        } else {
          setCcEmails([]);
        }
      }
    }
  );

// In the useQuery for bookings, add better filtering parameters
const { data: bookingsData, isLoading: isLoadingBookings, error: bookingsError, refetch: refetchBookings } = useQuery(
  ['bookings', currentPage, viewMode],
  () => {
    let url = `/api/bookings/vendor?page=${currentPage}&limit=${ITEMS_PER_PAGE}`;
    
    // Add filter parameter for the backend
    if (viewMode) {
      url += `&filter=${viewMode}`;
    }
    
    return axios.get(url).then(res => res.data);
  },
  {
    keepPreviousData: true,
    retry: 1,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    staleTime: 0
  }
);

// Make sure to add this useEffect for immediate refresh when changing view modes
useEffect(() => {
  setCurrentPage(1);
  // Force a refetch when viewMode changes
  queryClient.invalidateQueries(['bookings']);
}, [viewMode, queryClient]);

  const { data: customerBookings, isLoading: isLoadingCustomerBookings } = useQuery(
    ['vendorCustomerBookings', selectedCustomer],
    () => selectedCustomer ? axios.get(`/api/bookings/vendor/customer/${selectedCustomer}`).then(res => res.data) : null,
    {
      enabled: !!selectedCustomer,
      onSuccess: (data) => {
        if (data && data.length > 0) {
          // Assume the first booking has the customer details we need
          const firstBooking = data[0];
          setCustomerDetails({
            name: firstBooking.customer_name,
            email: firstBooking.customer_email,
            phone: firstBooking.customer_phone || 'Not provided'
          });
        }
      }
    }
  );

  const updateEmailSettingsMutation = useMutation(
    (newSettings) => axios.post('/api/bookings/vendor/email-settings', newSettings),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('emailSettings');
        setToast({
          type: 'success',
          message: 'Email settings updated successfully'
        });
      },
      onError: (error) => {
        setToast({
          type: 'error',
          message: error.response?.data?.error || 'Failed to update email settings'
        });
      }
    }
  );

  const approveBookingMutation = useMutation(
    (bookingId) => axios.post(`/api/bookings/vendor/approve/${bookingId}`),
    {
      onMutate: (bookingId) => {
        setApprovingBookings(prev => ({ ...prev, [bookingId]: true }));
      },
      onSuccess: (_, bookingId) => {
        queryClient.invalidateQueries('bookings');
        queryClient.invalidateQueries('customerBookings');
        setApprovingBookings(prev => ({ ...prev, [bookingId]: false }));
        setToast({
          type: 'success',
          message: 'Booking approved successfully'
        });
      },
      onError: (error, bookingId) => {
        setToast({
          type: 'error',
          message: error.response?.data?.error || 'Failed to approve booking'
        });
        setApprovingBookings(prev => ({ ...prev, [bookingId]: false }));
      }
    }
  );

  const debouncedHandleEmailSettingsUpdate = useCallback(
    debounce(async () => {
      updateEmailSettingsMutation.mutate({ 
        notificationEmail, 
        ccEmails: ccEmails.join(', ') 
      });
    }, 500),
    [updateEmailSettingsMutation, notificationEmail, ccEmails]
  );

  const handleEmailSettingsUpdate = (e) => {
    e?.preventDefault();
    // Call immediately instead of debounced
    updateEmailSettingsMutation.mutate({ 
      notificationEmail, 
      ccEmails: ccEmails.join(', ') 
    });
  };


const handleApproveBooking = async (bookingId) => {
  approveBookingMutation.mutate(bookingId);
};

const handleAddCcEmail = () => {
  if (ccEmailInput && !ccEmails.includes(ccEmailInput)) {
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(ccEmailInput)) {
      setCcEmails([...ccEmails, ccEmailInput]);
      setCcEmailInput('');
    }
  }
};

  const handleRemoveCcEmail = (emailToRemove) => {
    setCcEmails(ccEmails.filter(email => email !== emailToRemove));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddCcEmail();
    }
  };

  // Filter and format booking dates for display
  const formatBookingDate = (dateTime) => {
    const date = new Date(dateTime);
    return date.toLocaleDateString();
  };
  
  const formatBookingTime = (dateTime) => {
    const date = new Date(dateTime);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isToday = (dateTime) => {
    const today = new Date();
    const date = new Date(dateTime);
    return date.getDate() === today.getDate() && 
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Determine if a date is in the future
  const isFutureDate = (dateTime) => {
    return new Date(dateTime) > new Date();
  };

  const getStatusBadgeClass = (status) => {
    switch(status) {
      case 'approved':
        return 'bm-status-badge approved';
      case 'pending':
        return 'bm-status-badge pending';
      case 'cancelled':
        return 'bm-status-badge cancelled';
      default:
        return 'bm-status-badge';
    }
  };

  const getBookingCardClass = (dateTime) => {
    if (isToday(dateTime)) {
      return 'bm-booking-card today';
    } else if (isFutureDate(dateTime)) {
      return 'bm-booking-card future';
    } else {
      return 'bm-booking-card past';
    }
  };

  if (isLoadingEmailSettings && isLoadingBookings) {
    return (
      <div className="bm-loading">
        <div className="bm-spinner"></div>
        <span>Loading booking management...</span>
      </div>
    );
  }

  if (bookingsError) {
    return (
      <div className="bm-error-container">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p>{bookingsError.message || 'An error occurred while loading bookings'}</p>
        <button className="bm-button secondary" onClick={() => refetchBookings()}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="bm-container">
      {toast && (
        <div className={`bm-toast ${toast.type}`}>
          <div className="bm-toast-content">
            <span className={`bm-toast-icon ${toast.type}`}>
              {toast.type === 'success' ? '✓' : '✕'}
            </span>
            <span className="bm-toast-message">{toast.message}</span>
          </div>
        </div>
      )}

      <div className="bm-header">
        <div className="bm-header-content">
          <h1>Booking Management</h1>
          <p className="bm-header-subtitle">Manage customer reservations and booking settings</p>
        </div>
      </div>

      {/* Email Settings Card */}
      <div className="bm-card">
      <div 
        className="bm-card-header"
        onClick={() => setEmailSettingsExpanded(!emailSettingsExpanded)}
        style={{ cursor: 'pointer' }}
      >
        <h2>Email Notification Settings</h2>
        <span className="bm-toggle-indicator">
          {emailSettingsExpanded ? '▲' : '▼'}
        </span>
        <button 
          className="bm-toggle-button" 
          onClick={(e) => {
            e.stopPropagation();
            setEmailSettingsExpanded(!emailSettingsExpanded);
          }}
          aria-label={emailSettingsExpanded ? "Collapse settings" : "Expand settings"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {emailSettingsExpanded ? (
              <polyline points="18 15 12 9 6 15"></polyline>
            ) : (
              <polyline points="6 9 12 15 18 9"></polyline>
            )}
          </svg>
        </button>
      </div>
        
        {emailSettingsExpanded && (
          <div className="bm-card-body">
            <div className="bm-info-box">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
              <span>Set the email address where booking notifications will be sent. You can add multiple CC addresses to keep your team informed.</span>
            </div>
            
            <form onSubmit={(e) => e.preventDefault()} className="bm-form">
              <div className="bm-form-group">
                <label htmlFor="notificationEmail">
                  Primary Notification Email
                  <InfoTooltip 
                    text="Main email address where all booking notifications will be sent" 
                    variant="vendor"
                  />
                </label>
                <input
                  type="email"
                  id="notificationEmail"
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                  className="bm-input"
                  placeholder="e.g., bookings@yourbusiness.com"
                  required
                />
              </div>
              
              <div className="bm-form-group">
                <label htmlFor="ccEmails">
                  CC Emails
                  <InfoTooltip 
                    text="Additional email addresses that will receive copies of booking notifications" 
                    variant="vendor"
                  />
                </label>
                <div className="bm-cc-emails-container">
                  <div className="bm-cc-emails-input-container">
                    <input
                      type="email"
                      id="ccEmails"
                      value={ccEmailInput}
                      onChange={(e) => setCcEmailInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className="bm-input"
                      placeholder="Type email and press Enter to add"
                    />
                    <button 
                      type="button"
                      className="bm-add-email-btn"
                      onClick={handleAddCcEmail}
                      disabled={!ccEmailInput}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                      </svg>
                      Add
                    </button>
                  </div>
                  
                  {ccEmails.length > 0 && (
                    <div className="bm-cc-emails-list">
                      {ccEmails.map((email, index) => (
                        <div key={index} className="bm-cc-email-tag">
                          <span>{email}</span>
                          <button 
                            type="button"
                            className="bm-remove-email-btn"
                            onClick={() => handleRemoveCcEmail(email)}
                            aria-label={`Remove ${email}`}
                          >
                            &times;
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <small className="bm-form-helper">
                  Enter email addresses one at a time. Press Enter or use the Add button after each one.
                </small>
              </div>
              
              <div className="bm-form-actions">
                <button 
                  type="button" 
                  className="bm-button primary"
                  onClick={handleEmailSettingsUpdate}
                  disabled={updateEmailSettingsMutation.isLoading}
                >
                  {updateEmailSettingsMutation.isLoading ? (
                    <>
                      <div className="bm-spinner-sm"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    'Save Email Settings'
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Booking Requests Section */}
      <div className="bm-section-title">
        <div className="bm-section-heading">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <h3>Booking Requests</h3>
        </div>
        
        <div className="bm-view-filter">
          <button 
            className={`bm-filter-btn ${viewMode === 'upcoming' ? 'active' : ''}`} 
            onClick={() => setViewMode('upcoming')}
          >
            Upcoming
          </button>
          <button 
            className={`bm-filter-btn ${viewMode === 'pending' ? 'active' : ''}`} 
            onClick={() => setViewMode('pending')}
          >
            Pending
          </button>
          <button 
            className={`bm-filter-btn ${viewMode === 'all' ? 'active' : ''}`} 
            onClick={() => setViewMode('all')}
          >
            All
          </button>
        </div>
      </div>

      {/* Selected Customer Profile */}
      {selectedCustomer && (
        <div className="bm-customer-profile">
          <div className="bm-customer-profile-header">
            <div className="bm-customer-avatar">
              {customerDetails?.name?.charAt(0).toUpperCase() || '?'}
            </div>
            <div className="bm-customer-info">
              <h3>{customerDetails?.name || 'Customer'}</h3>
              <div className="bm-customer-contact">
                {customerDetails?.email && (
                  <div className="bm-customer-detail">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    <span>{customerDetails.email}</span>
                  </div>
                )}
                {customerDetails?.phone && (
                  <div className="bm-customer-detail">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    <span>{customerDetails.phone}</span>
                  </div>
                )}
              </div>
            </div>
            <button 
              className="bm-button-icon" 
              onClick={() => setSelectedCustomer(null)}
              aria-label="Close customer profile"
            >
              &times;
            </button>
          </div>
          
          <div className="bm-customer-bookings-section">
            <h4>Booking History</h4>
            {isLoadingCustomerBookings ? (
              <div className="bm-loading-inline">
                <div className="bm-spinner-sm"></div>
                <span>Loading bookings...</span>
              </div>
            ) : customerBookings?.length > 0 ? (
              <div className="bm-customer-bookings-grid">
                {customerBookings.map(booking => (
                  <div key={booking.id} className={getBookingCardClass(booking.date_time)}>
                    <div className="bm-booking-card-header">
                      <div className={getStatusBadgeClass(booking.status)}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </div>
                      <div className="bm-booking-date">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        <span>{formatBookingDate(booking.date_time)}</span>
                      </div>
                    </div>
                    <div className="bm-booking-card-body">
                      <div className="bm-booking-detail">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <span>{formatBookingTime(booking.date_time)}</span>
                      </div>
                      <div className="bm-booking-detail">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        <span>{booking.number_of_people} {booking.number_of_people === 1 ? 'person' : 'people'}</span>
                      </div>
                      {booking.special_requests && (
                        <div className="bm-booking-special-requests">
                          <p>"{booking.special_requests}"</p>
                        </div>
                      )}
                    </div>
                    {booking.status === 'pending' && (
                      <div className="bm-booking-card-footer">
                        <button 
                          className="bm-button primary sm"
                          onClick={() => handleApproveBooking(booking.id)}
                          disabled={approvingBookings[booking.id]}
                        >
                          {approvingBookings[booking.id] ? (
                            <>
                              <div className="bm-spinner-sm"></div>
                              <span>Approving...</span>
                            </>
                          ) : (
                            'Approve'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bm-empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                <p>No booking history found for this customer</p>
              </div>
            )}
            <div className="bm-customer-profile-actions">
              <button 
                className="bm-button secondary"
                onClick={() => setSelectedCustomer(null)}
              >
                Close Customer Profile
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Display Bookings */}
      {!selectedCustomer && (
        <>
          {isLoadingBookings ? (
            <div className="bm-loading-inline">
              <div className="bm-spinner-sm"></div>
              <span>Loading bookings...</span>
            </div>
          ) : bookingsData?.bookings?.length > 0 ? (
            <>
              <div className="bm-bookings-grid">
                {bookingsData.bookings.map(booking => (
                  <div key={booking.id} className={getBookingCardClass(booking.date_time)}>
                    <div className="bm-booking-card-header">
                      <div className={getStatusBadgeClass(booking.status)}>
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </div>
                      <div className="bm-booking-date">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                          <line x1="16" y1="2" x2="16" y2="6"></line>
                          <line x1="8" y1="2" x2="8" y2="6"></line>
                          <line x1="3" y1="10" x2="21" y2="10"></line>
                        </svg>
                        <span>{formatBookingDate(booking.date_time)}</span>
                        {isToday(booking.date_time) && <span className="bm-today-indicator">Today</span>}
                      </div>
                    </div>
                    <div className="bm-booking-card-body">
                      <div className="bm-booking-customer">
                        <button 
                          className="bm-customer-button"
                          onClick={() => setSelectedCustomer(booking.customer_id)}
                          title="Click to view customer profile"
                        >
                          <div className="bm-customer-avatar-sm">
                            {booking.customer_name.charAt(0).toUpperCase()}
                          </div>
                          <span className="bm-customer-name">{booking.customer_name}</span>
                          <span className="bm-view-profile-text">View Profile</span>
                        </button>
                      </div>
                      <div className="bm-booking-details">
                        <div className="bm-booking-detail">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                          <span>{formatBookingTime(booking.date_time)}</span>
                        </div>
                        <div className="bm-booking-detail">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                          </svg>
                          <span>{booking.number_of_people} {booking.number_of_people === 1 ? 'person' : 'people'}</span>
                        </div>
                      </div>
                      {booking.special_requests && (
                        <div className="bm-booking-special-requests">
                          <p>"{booking.special_requests}"</p>
                        </div>
                      )}
                    </div>
                    {booking.status === 'pending' && (
                      <div className="bm-booking-card-footer">
                        <button 
                          className="bm-button primary sm"
                          onClick={() => handleApproveBooking(booking.id)}
                          disabled={approvingBookings[booking.id]}
                        >
                          {approvingBookings[booking.id] ? (
                            <>
                              <div className="bm-spinner-sm"></div>
                              <span>Approving...</span>
                            </>
                          ) : (
                            'Approve'
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {bookingsData.totalPages > 1 && (
                <div className="bm-pagination">
                  <button 
                    className="bm-pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                    disabled={currentPage === 1}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    <span>Previous</span>
                  </button>
                  <span className="bm-pagination-info">Page {currentPage} of {bookingsData.totalPages}</span>
                  <button 
                    className="bm-pagination-btn"
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, bookingsData.totalPages))} 
                    disabled={currentPage === bookingsData.totalPages}
                  >
                    <span>Next</span>
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="bm-empty-state">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              <p>No bookings found</p>
              {viewMode !== 'all' && (
                <button className="bm-button secondary" onClick={() => setViewMode('all')}>
                  View All Bookings
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default BookingManagement;